import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, TrendingUp, Gift, FileText, Package } from "lucide-react";
import { toast } from "sonner";

const PRODUCT_OPTIONS = [
  { id: "all", name: "All products" },
  { id: "social-cinematic", name: "30s Listing" },
  { id: "most-popular", name: "60s Listing" },
  { id: "vision-standard", name: "Vision Standard" },
  { id: "vision-professional", name: "Vision Professional" },
];

function Stat({ label, value, sub }) {
  return (
    <div className="glass rounded-2xl p-6">
      <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">{label}</p>
      <p className="font-mono text-3xl text-white">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

export default function Admin() {
  const [tab, setTab] = useState("analytics");
  const [analytics, setAnalytics] = useState(null);
  const [promos, setPromos] = useState([]);
  const [orders, setOrders] = useState([]);
  const [intakes, setIntakes] = useState([]);

  const loadAll = async () => {
    const [a, p, o, i] = await Promise.all([
      api.get("/admin/analytics"), api.get("/promo"), api.get("/admin/orders"), api.get("/admin/custom"),
    ]);
    setAnalytics(a.data); setPromos(p.data); setOrders(o.data); setIntakes(i.data);
  };
  useEffect(() => { loadAll(); }, []);

  return (
    <div className="bg-[#090A0F] min-h-screen"><Header />
      <div className="max-w-7xl mx-auto px-6 pt-32 pb-16 fade-up">
        <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#D4AF37] mb-2">PropMotion Operations</p>
        <h1 className="font-display text-4xl text-white mb-8">Admin Console</h1>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-[#12141D] border border-slate-800 mb-8">
            <TabsTrigger value="analytics" data-testid="tab-analytics"><TrendingUp className="w-4 h-4 mr-1.5" /> Analytics</TabsTrigger>
            <TabsTrigger value="promos" data-testid="tab-promos"><Gift className="w-4 h-4 mr-1.5" /> Promo & Test Codes</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders"><Package className="w-4 h-4 mr-1.5" /> Orders</TabsTrigger>
            <TabsTrigger value="custom" data-testid="tab-custom"><FileText className="w-4 h-4 mr-1.5" /> Custom Briefs</TabsTrigger>
          </TabsList>

          <TabsContent value="analytics">
            {analytics && (
              <>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
                  <Stat label="Revenue (paid)" value={`$${analytics.revenue.toLocaleString()}`} sub="Excludes complimentary" />
                  <Stat label="Total orders" value={analytics.total_orders} sub={`${analytics.comp_orders} complimentary`} />
                  <Stat label="Delivered" value={analytics.delivered} sub={`${analytics.in_production} in production`} />
                  <Stat label="SLA met" value={`${analytics.sla_met}/${analytics.sla_total}`} sub={`${analytics.qa_repairs} QA repairs`} />
                </div>
                <div className="grid sm:grid-cols-3 gap-5 mb-8">
                  <Stat label="Retail value produced" value={`$${analytics.retail_value_total.toLocaleString()}`} />
                  <Stat label="Total discounts" value={`$${analytics.discount_total.toLocaleString()}`} />
                  <Stat label="Comp orders" value={analytics.comp_orders} sub="Count toward production/QA, not revenue" />
                </div>
                <div className="glass rounded-2xl p-6">
                  <h3 className="font-display text-2xl text-white mb-4">Promo performance</h3>
                  <table className="w-full text-sm">
                    <thead><tr className="text-slate-400 text-left border-b border-slate-800">
                      <th className="py-2">Code</th><th>Redemptions</th><th>Retail</th><th>Discount</th><th>Collected</th></tr></thead>
                    <tbody>
                      {analytics.promo_stats.map((s) => (
                        <tr key={s.code} className="border-b border-slate-800/50" data-testid={`promo-stat-${s.code}`}>
                          <td className="py-3 font-mono text-[#D4AF37]">{s.code}</td>
                          <td className="text-white">{s.redemptions}</td>
                          <td className="text-slate-300">${s.retail_value.toLocaleString()}</td>
                          <td className="text-slate-300">${s.discount.toLocaleString()}</td>
                          <td className="text-slate-300">${s.collected_revenue.toLocaleString()}</td>
                        </tr>
                      ))}
                      {analytics.promo_stats.length === 0 && <tr><td colSpan={5} className="py-4 text-slate-500">No codes yet.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="promos">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-display text-2xl text-white">Promo & Test Codes</h3>
              <div className="flex gap-3">
                <CompOrderDialog onDone={loadAll} />
                <PromoDialog onDone={loadAll} />
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="promo-grid">
              {promos.map((p) => (
                <div key={p.id} className="glass rounded-2xl p-5" data-testid={`promo-card-${p.code}`}>
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-mono text-lg text-[#D4AF37]">{p.code}</span>
                    <button onClick={async () => { await api.delete(`/promo/${p.id}`); toast.success("Deleted"); loadAll(); }}
                      className="text-slate-500 hover:text-red-400" data-testid={`delete-promo-${p.code}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <p className="text-sm text-slate-400 mb-3">{p.description || "—"}</p>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <Badge className="bg-slate-700/40 text-slate-200">{p.discount_type === "free_100" ? "100% Free" : p.discount_type === "percentage" ? `${p.discount_value}%` : `$${p.discount_value}`}</Badge>
                    <Badge className={p.active ? "bg-emerald-500/20 text-emerald-400" : "bg-slate-600/30 text-slate-400"}>{p.active ? "Active" : "Inactive"}</Badge>
                    <Badge className="bg-slate-700/40 text-slate-200">{p.redemptions_count} redeemed</Badge>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="orders">
            <div className="glass rounded-2xl p-6 overflow-x-auto" data-testid="admin-orders-table">
              <table className="w-full text-sm min-w-[700px]">
                <thead><tr className="text-slate-400 text-left border-b border-slate-800">
                  <th className="py-2">Order</th><th>Customer</th><th>Product</th><th>Status</th><th>Collected</th><th>Type</th></tr></thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} className="border-b border-slate-800/50" data-testid={`admin-order-${o.order_number}`}>
                      <td className="py-3 font-mono text-slate-300">{o.order_number}</td>
                      <td className="text-slate-300">{o.customer_email}</td>
                      <td className="text-white">{o.product_name}</td>
                      <td><Badge className="bg-blue-500/20 text-blue-400">{o.status.replace(/_/g, " ")}</Badge></td>
                      <td className="font-mono text-[#D4AF37]">${o.collected_revenue?.toLocaleString()}</td>
                      <td>{o.is_complimentary ? <Badge className="bg-[#D4AF37]/20 text-[#D4AF37]">Comp</Badge> : <span className="text-slate-500">Paid</span>}</td>
                    </tr>
                  ))}
                  {orders.length === 0 && <tr><td colSpan={6} className="py-4 text-slate-500">No orders yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="custom">
            <div className="space-y-4" data-testid="custom-briefs">
              {intakes.map((it) => (
                <details key={it.id} className="glass rounded-2xl p-6" data-testid={`brief-${it.id}`}>
                  <summary className="cursor-pointer flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-display text-xl text-white">{it.intake.project_name}</p>
                      <p className="text-sm text-slate-400">{it.contact_name} · {it.contact_email}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className="bg-purple-500/20 text-purple-300">Complexity {it.analysis.complexity_score}</Badge>
                      {it.crm_synced && <Badge className="bg-emerald-500/20 text-emerald-400" data-testid={`ghl-badge-${it.id}`}>GHL Synced</Badge>}
                      <Badge className="bg-slate-700/40 text-slate-200">{it.status.replace(/_/g, " ")}</Badge>
                      <span className="font-mono text-[#D4AF37]">${it.analysis.recommended_price_target.toLocaleString()}</span>
                    </div>
                  </summary>
                  <div className="mt-5 grid md:grid-cols-2 gap-6 text-sm">
                    <div>
                      <p className="text-slate-500 text-xs uppercase mb-1">Summary</p>
                      <p className="text-slate-300 mb-4">{it.analysis.summary}</p>
                      <p className="text-slate-500 text-xs uppercase mb-1">Recommended pricing</p>
                      <p className="text-slate-300 mb-4">${it.analysis.recommended_price_low.toLocaleString()} / ${it.analysis.recommended_price_target.toLocaleString()} / ${it.analysis.recommended_price_high.toLocaleString()}</p>
                      <p className="text-slate-500 text-xs uppercase mb-1">Close probability</p>
                      <p className="text-slate-300">{Math.round(it.analysis.close_probability * 100)}%</p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-xs uppercase mb-1">Deliverables</p>
                      <ul className="text-slate-300 mb-4 list-disc pl-4">{it.analysis.recommended_deliverables.map((d, i) => <li key={i}>{d}</li>)}</ul>
                      <p className="text-slate-500 text-xs uppercase mb-1">Risks</p>
                      <ul className="text-slate-300 mb-4 list-disc pl-4">{it.analysis.risks.length ? it.analysis.risks.map((d, i) => <li key={i}>{d}</li>) : <li>None flagged</li>}</ul>
                      {it.booking && <p className="text-emerald-400">Call: {it.booking.preferred_date} {it.booking.preferred_time}</p>}
                    </div>
                  </div>
                  <QuoteDialog intake={it} onDone={loadAll} />
                </details>
              ))}
              {intakes.length === 0 && <p className="text-slate-500">No custom intakes yet.</p>}
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
}

function PromoDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", description: "", discount_type: "free_100", discount_value: 0, eligible_products: ["all"], applies_to_addons: true, max_redemptions: "", per_user_limit: "", active: true });
  const save = async () => {
    try {
      await api.post("/promo", {
        ...form,
        discount_value: Number(form.discount_value) || 0,
        max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : null,
        per_user_limit: form.per_user_limit ? Number(form.per_user_limit) : null,
      });
      toast.success("Code created"); setOpen(false); onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-[#D4AF37] text-black hover:bg-[#E2C044] rounded-full" data-testid="create-promo-button"><Plus className="w-4 h-4 mr-1.5" /> New Code</Button></DialogTrigger>
      <DialogContent className="bg-[#12141D] border-slate-800 text-white max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl">Create promo / test code</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="mt-1.5 bg-[#090A0F] border-slate-700 font-mono" data-testid="promo-code-input" /></div>
          <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 bg-[#090A0F] border-slate-700" data-testid="promo-desc-input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Discount type</Label>
              <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                <SelectTrigger className="mt-1.5 bg-[#090A0F] border-slate-700" data-testid="promo-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#12141D] border-slate-700 text-white">
                  <SelectItem value="free_100">100% Free</SelectItem>
                  <SelectItem value="percentage">Percentage</SelectItem>
                  <SelectItem value="fixed">Fixed amount</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.discount_type !== "free_100" && (
              <div><Label>Value</Label><Input type="number" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} className="mt-1.5 bg-[#090A0F] border-slate-700" data-testid="promo-value-input" /></div>
            )}
          </div>
          <div><Label>Eligible product</Label>
            <Select value={form.eligible_products[0]} onValueChange={(v) => setForm({ ...form, eligible_products: [v] })}>
              <SelectTrigger className="mt-1.5 bg-[#090A0F] border-slate-700" data-testid="promo-product-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#12141D] border-slate-700 text-white">
                {PRODUCT_OPTIONS.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Max redemptions</Label><Input type="number" value={form.max_redemptions} onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })} placeholder="unlimited" className="mt-1.5 bg-[#090A0F] border-slate-700" data-testid="promo-max-input" /></div>
            <div><Label>Per-user limit</Label><Input type="number" value={form.per_user_limit} onChange={(e) => setForm({ ...form, per_user_limit: e.target.value })} placeholder="unlimited" className="mt-1.5 bg-[#090A0F] border-slate-700" data-testid="promo-peruser-input" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.applies_to_addons} onCheckedChange={(v) => setForm({ ...form, applies_to_addons: !!v })} className="border-[#D4AF37]/50 data-[state=checked]:bg-[#D4AF37]" /> Applies to add-ons</label>
        </div>
        <DialogFooter><Button onClick={save} className="bg-[#D4AF37] text-black hover:bg-[#E2C044] rounded-full" data-testid="save-promo-button">Create Code</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompOrderDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_email: "", product_id: "most-popular", notes: "", property: { name: "" } });
  const save = async () => {
    try {
      await api.post("/admin/comp-order", { ...form, addons: [], property: { name: form.property.name } });
      toast.success("Complimentary order created & queued for production"); setOpen(false); onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" className="border-[#D4AF37]/40 text-[#D4AF37] rounded-full" data-testid="comp-order-button"><Gift className="w-4 h-4 mr-1.5" /> Comp Order</Button></DialogTrigger>
      <DialogContent className="bg-[#12141D] border-slate-800 text-white">
        <DialogHeader><DialogTitle className="font-display text-2xl">Direct complimentary order</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Customer email (existing account)</Label><Input value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="mt-1.5 bg-[#090A0F] border-slate-700" data-testid="comp-email-input" /></div>
          <div><Label>Product</Label>
            <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v })}>
              <SelectTrigger className="mt-1.5 bg-[#090A0F] border-slate-700" data-testid="comp-product-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#12141D] border-slate-700 text-white">
                {PRODUCT_OPTIONS.filter((p) => p.id !== "all").map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Property name</Label><Input value={form.property.name} onChange={(e) => setForm({ ...form, property: { name: e.target.value } })} className="mt-1.5 bg-[#090A0F] border-slate-700" data-testid="comp-property-input" /></div>
          <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 bg-[#090A0F] border-slate-700" data-testid="comp-notes-input" /></div>
          <p className="text-xs text-slate-500 flex items-center gap-2"><Checkbox checked disabled className="border-[#D4AF37]/50 data-[state=checked]:bg-[#D4AF37]" /> Complimentary / Internal Test — no payment, full production.</p>
        </div>
        <DialogFooter><Button onClick={save} className="bg-[#D4AF37] text-black hover:bg-[#E2C044] rounded-full" data-testid="save-comp-button">Create Order</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QuoteDialog({ intake, onDone }) {
  const [open, setOpen] = useState(false);
  const a = intake.analysis;
  const [form, setForm] = useState({
    scope_summary: a.summary,
    deliverables: a.recommended_deliverables.join("\n"),
    price: a.recommended_price_target,
    deposit: Math.round(a.recommended_price_target * 0.5),
    timeline: a.recommended_timeline,
  });
  const save = async () => {
    try {
      const { data } = await api.post(`/admin/custom/${intake.id}/quote`, {
        scope_summary: form.scope_summary,
        deliverables: form.deliverables.split("\n").filter(Boolean),
        price: Number(form.price), deposit: Number(form.deposit),
        timeline: form.timeline, origin_url: window.location.origin,
      });
      toast.success("Quote created — deposit link generated");
      window.open(data.quote.checkout_url, "_blank");
      setOpen(false); onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="mt-5 bg-[#D4AF37] text-black hover:bg-[#E2C044] rounded-full" data-testid={`quote-button-${intake.id}`}>Confirm Scope & Create Quote</Button></DialogTrigger>
      <DialogContent className="bg-[#12141D] border-slate-800 text-white max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl">Custom quote</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Scope summary</Label><Textarea value={form.scope_summary} onChange={(e) => setForm({ ...form, scope_summary: e.target.value })} className="mt-1.5 bg-[#090A0F] border-slate-700" data-testid="quote-scope-input" /></div>
          <div><Label>Deliverables (one per line)</Label><Textarea value={form.deliverables} onChange={(e) => setForm({ ...form, deliverables: e.target.value })} className="mt-1.5 bg-[#090A0F] border-slate-700 min-h-[100px]" data-testid="quote-deliverables-input" /></div>
          <div className="grid grid-cols-3 gap-4">
            <div><Label>Price</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1.5 bg-[#090A0F] border-slate-700" data-testid="quote-price-input" /></div>
            <div><Label>Deposit</Label><Input type="number" value={form.deposit} onChange={(e) => setForm({ ...form, deposit: e.target.value })} className="mt-1.5 bg-[#090A0F] border-slate-700" data-testid="quote-deposit-input" /></div>
            <div><Label>Timeline</Label><Input value={form.timeline} onChange={(e) => setForm({ ...form, timeline: e.target.value })} className="mt-1.5 bg-[#090A0F] border-slate-700" data-testid="quote-timeline-input" /></div>
          </div>
        </div>
        <DialogFooter><Button onClick={save} className="bg-[#D4AF37] text-black hover:bg-[#E2C044] rounded-full" data-testid="save-quote-button">Generate Deposit Link</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
